/**
 * Reminder Schedule Modal
 *
 * Modal for scheduling follow-up reminder emails to selected invitations.
 * Provides pre-built reminder templates and custom message support,
 * with immediate or scheduled delivery options.
 *
 * Features:
 * - Pre-built reminder templates (Gentle Reminder, RSVP Request, Last Chance)
 * - Custom message textarea with template preview
 * - Immediate send or scheduled delivery with date/time picker
 * - Loading state during send operation
 * - Success/error feedback via toast notifications
 *
 * Used by:
 * - InvitationTrackingTable (bulk action bar "Remind" button)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Bell, Clock, Send, Loader2, FileText } from 'lucide-react';
import { Modal } from '@shared/components/ui/Modal';
import { invitationTrackingApi } from '@shared/api/invitationTrackingApi';
import { toast } from '@shared/utils/notifications';
import type { EventType } from '@shared/types/invitationTracking';

// ============================================================
// PROPS
// ============================================================

interface ReminderScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIds: number[];
  eventType: 'usi' | 'hmc';
  onSuccess?: () => void;
}

// ============================================================
// REMINDER TEMPLATES
// ============================================================

type TemplateKey = 'gentle' | 'rsvp' | 'last_chance' | 'custom';

interface ReminderTemplate {
  key: TemplateKey;
  label: string;
  description: string;
  body: string;
}

const REMINDER_TEMPLATES: ReminderTemplate[] = [
  {
    key: 'gentle',
    label: 'Gentle Reminder',
    description: 'A friendly reminder about the upcoming event',
    body: 'Hi {name},\n\nJust a friendly reminder about our upcoming event. We would love to see you there!\n\nPlease let us know if you have any questions or need additional information.\n\nBest regards,\nBenefits Outreach Team',
  },
  {
    key: 'rsvp',
    label: 'RSVP Request',
    description: 'Request an RSVP response from the recipient',
    body: 'Hi {name},\n\nWe haven\'t received your RSVP yet for our upcoming event. Could you please let us know if you will be attending?\n\nYour response helps us plan and prepare for the event.\n\nThank you,\nBenefits Outreach Team',
  },
  {
    key: 'last_chance',
    label: 'Last Chance',
    description: 'A final reminder before the event deadline',
    body: 'Hi {name},\n\nThis is a final reminder for our event. Registration closes soon, so please confirm your attendance at your earliest convenience.\n\nWe hope to see you there!\n\nBest regards,\nBenefits Outreach Team',
  },
  {
    key: 'custom',
    label: 'Custom Message',
    description: 'Write your own reminder message',
    body: '',
  },
];

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ReminderScheduleModal({
  isOpen,
  onClose,
  selectedIds,
  eventType,
  onSuccess,
}: ReminderScheduleModalProps) {
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateKey>('gentle');
  const [customMessage, setCustomMessage] = useState('');
  const [scheduleMode, setScheduleMode] = useState<'immediate' | 'scheduled'>('immediate');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // Send state
  const [isSending, setIsSending] = useState(false);

  // Derived: active template text
  const activeTemplateText = useMemo(() => {
    if (selectedTemplate === 'custom') {
      return customMessage;
    }
    const template = REMINDER_TEMPLATES.find((t) => t.key === selectedTemplate);
    return template?.body || '';
  }, [selectedTemplate, customMessage]);

  // Derived: can send?
  const canSend = useMemo(() => {
    if (selectedIds.length === 0) return false;
    if (!activeTemplateText.trim()) return false;
    if (scheduleMode === 'scheduled' && (!scheduledDate || !scheduledTime)) return false;
    return true;
  }, [selectedIds, activeTemplateText, scheduleMode, scheduledDate, scheduledTime]);

  // Reset form when modal closes
  const handleClose = useCallback(() => {
    setSelectedTemplate('gentle');
    setCustomMessage('');
    setScheduleMode('immediate');
    setScheduledDate('');
    setScheduledTime('');
    setIsSending(false);
    onClose();
  }, [onClose]);

  // Handle send
  const handleSend = useCallback(async () => {
    if (!canSend || isSending) return;

    setIsSending(true);

    try {
      const result = await invitationTrackingApi.bulkRemind(
        selectedIds,
        activeTemplateText
      );

      if (result.sent > 0) {
        toast.success(
          `Sent ${result.sent} reminder${result.sent > 1 ? 's' : ''} successfully`
        );
      }

      if (result.failed > 0) {
        toast.error(
          `${result.failed} reminder${result.failed > 1 ? 's' : ''} failed: ${result.errors.join(', ')}`
        );
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reminders';
      toast.error(message);
    } finally {
      setIsSending(false);
    }
  }, [canSend, isSending, selectedIds, activeTemplateText, onSuccess, handleClose]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Schedule Follow-up Reminder"
      size="md"
    >
      <div className="space-y-6">
        {/* Header info */}
        <div className="flex items-center gap-3 p-3 bg-brand-500/10 border border-brand-500/20 rounded-lg">
          <Bell className="w-5 h-5 text-brand-400 shrink-0" />
          <p className="text-sm text-dark-text">
            Sending reminder to{' '}
            <span className="font-semibold text-brand-400">
              {selectedIds.length}
            </span>{' '}
            invitation{selectedIds.length !== 1 ? 's' : ''} ({eventType.toUpperCase()} Events)
          </p>
        </div>

        {/* ====================== Template Selection ====================== */}
        <div>
          <h3 className="text-sm font-medium text-dark-text mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-dark-muted" />
            Reminder Template
          </h3>
          <div className="space-y-2">
            {REMINDER_TEMPLATES.map((template) => (
              <label
                key={template.key}
                className={`
                  flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150
                  ${selectedTemplate === template.key
                    ? 'border-brand-500/50 bg-brand-500/10'
                    : 'border-dark-border/30 bg-dark-surface/30 hover:border-dark-border/50'
                  }
                `}
              >
                <input
                  type="radio"
                  name="reminderTemplate"
                  value={template.key}
                  checked={selectedTemplate === template.key}
                  onChange={() => setSelectedTemplate(template.key)}
                  className="mt-1 accent-brand-500"
                />
                <div className="min-w-0 flex-1">
                  <span className="text-sm font-medium text-dark-text">
                    {template.label}
                  </span>
                  <p className="text-xs text-dark-muted mt-0.5">
                    {template.description}
                  </p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* ====================== Custom Message Textarea ====================== */}
        {selectedTemplate === 'custom' && (
          <div>
            <label className="block text-sm font-medium text-dark-text mb-2">
              Custom Message
            </label>
            <textarea
              value={customMessage}
              onChange={(e) => setCustomMessage(e.target.value)}
              rows={5}
              placeholder="Write your custom reminder message..."
              className="w-full px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text placeholder:text-dark-muted focus:outline-none focus:border-brand-500 transition-colors resize-none"
            />
          </div>
        )}

        {/* ====================== Template Preview ====================== */}
        {selectedTemplate !== 'custom' && activeTemplateText && (
          <div>
            <label className="block text-xs font-medium text-dark-muted mb-2 uppercase tracking-wider">
              Preview
            </label>
            <div className="p-3 bg-dark-surface/50 border border-dark-border/20 rounded-lg">
              <pre className="text-xs text-dark-muted whitespace-pre-wrap font-sans leading-relaxed">
                {activeTemplateText}
              </pre>
            </div>
          </div>
        )}

        {/* ====================== Schedule Options ====================== */}
        <div>
          <h3 className="text-sm font-medium text-dark-text mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-dark-muted" />
            Delivery Schedule
          </h3>
          <div className="space-y-2">
            {/* Send immediately */}
            <label
              className={`
                flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150
                ${scheduleMode === 'immediate'
                  ? 'border-brand-500/50 bg-brand-500/10'
                  : 'border-dark-border/30 bg-dark-surface/30 hover:border-dark-border/50'
                }
              `}
            >
              <input
                type="radio"
                name="scheduleMode"
                value="immediate"
                checked={scheduleMode === 'immediate'}
                onChange={() => setScheduleMode('immediate')}
                className="accent-brand-500"
              />
              <div>
                <span className="text-sm font-medium text-dark-text">
                  Send immediately
                </span>
                <p className="text-xs text-dark-muted mt-0.5">
                  Reminders will be sent right away
                </p>
              </div>
            </label>

            {/* Schedule for later */}
            <label
              className={`
                flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-150
                ${scheduleMode === 'scheduled'
                  ? 'border-brand-500/50 bg-brand-500/10'
                  : 'border-dark-border/30 bg-dark-surface/30 hover:border-dark-border/50'
                }
              `}
            >
              <input
                type="radio"
                name="scheduleMode"
                value="scheduled"
                checked={scheduleMode === 'scheduled'}
                onChange={() => setScheduleMode('scheduled')}
                className="mt-1 accent-brand-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-dark-text">
                  Schedule for later
                </span>
                <p className="text-xs text-dark-muted mt-0.5">
                  Choose a date and time to send the reminders
                </p>

                {/* Date/Time picker (shown when scheduled is selected) */}
                {scheduleMode === 'scheduled' && (
                  <div className="flex items-center gap-3 mt-3">
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text focus:outline-none focus:border-brand-500 transition-colors"
                    />
                    <input
                      type="time"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text focus:outline-none focus:border-brand-500 transition-colors"
                    />
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* ====================== Footer Actions ====================== */}
        <div className="flex justify-end gap-3 pt-4 border-t border-dark-border/30">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSending}
            className="px-6 py-2 text-sm font-medium text-dark-text bg-dark-surface border border-dark-border/30 rounded-xl hover:bg-dark-input disabled:opacity-50 transition-all duration-150"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={!canSend || isSending}
            className="inline-flex items-center gap-2 px-6 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
          >
            {isSending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Reminder ({selectedIds.length})
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default ReminderScheduleModal;
